import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const user = await login(email, pw);
      const roleHome = user.role === "admin" ? "/admin"
        : user.role === "case_handler" ? "/handler"
        : "/dashboard";
      const from = loc.state?.from;
      const validFrom = from && (
        (user.role === "admin" && from.startsWith("/admin")) ||
        (user.role === "case_handler" && (from.startsWith("/handler") || from.startsWith("/dashboard"))) ||
        (user.role === "student" && from.startsWith("/dashboard"))
      );
      nav(validFrom ? from : roleHome);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 bg-ink text-white">
        <Logo size="md"/>
        <div>
          <div className="text-xs uppercase tracking-widest opacity-60 font-semibold">Welcome back</div>
          <h2 className="mt-4 text-4xl lg:text-5xl font-bold tracking-tight">Right where you left off.</h2>
          <p className="mt-4 opacity-80 max-w-md">Your timeline, documents, and cohort, waiting for you.</p>
        </div>
        <div className="text-xs opacity-60">Every rule sourced from official government sites.</div>
      </div>

      <div className="p-6 md:p-12 flex items-center">
        <div className="w-full max-w-sm mx-auto">
          <div className="md:hidden mb-8"><Logo/></div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Log in</h1>
          <p className="mt-2 text-sm text-muted">Continue your exchange plan.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="moe-field">
              <input data-testid={TID.loginEmail} type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} placeholder=" " />
              <label>Email</label>
            </div>
            <div className="moe-field">
              <input data-testid={TID.loginPassword} type="password" required value={pw} onChange={(e)=>setPw(e.target.value)} placeholder=" " />
              <label>Password</label>
            </div>
            {err && <div className="text-sm text-red-600">{err}</div>}
            <button data-testid={TID.loginSubmit} disabled={busy} className="moe-btn moe-btn-primary w-full">
              {busy ? "Signing in…" : <>Log in <ArrowUpRight size={15}/></>}
            </button>
            <div className="text-right">
              <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-sm text-muted hover:text-elec">
                Forgot password?
              </Link>
            </div>
          </form>
          <div className="mt-6 text-sm text-muted">
            New here? <Link to="/signup" className="text-elec font-semibold">Start your intake →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
